import { useEffect, useState } from 'react';
import { Page } from '../components/Page.js';
import { Attachments } from '../components/Attachments.js';

type Notif = {
  uuid: string;
  rule_id: string | null;
  subject_type: string;
  subject_label: string | null;
  what: string;
  notified_date: string;
  method: string | null;
  reference: string | null;
  ledger_seq: number;
  created_at: string;
};

const METHOD: Record<string, string> = {
  online: 'Online form', in_person: 'In person', phone: 'Phone', letter: 'Letter', email: 'Email',
};
// Only attending in person discharges a duty (SOA 2003 s.87); flag the rest.
const NON_DISCHARGING = new Set(['online', 'phone', 'letter', 'email']);

export function Notifications() {
  const [items, setItems] = useState<Notif[]>([]);
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');

  useEffect(() => {
    fetch('/api/notifications')
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then((r) => { setItems(r.notifications ?? []); setStatus('ok'); })
      .catch(() => setStatus('error'));
  }, []);

  return (
    <Page
      eyebrow="Records"
      title="Notifications"
      actions={<span className="pill pill-ok"><span className="dot dot-ok" />the legal record · anchored</span>}
    >
      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Each entry is a record of telling the police something — the act that actually discharges a
          duty. Every one is written to the tamper-evident ledger, so you can later prove <b>when</b> you
          reported it. Create these from <b>What I need to do</b> so they automatically clear the matching
          deadline.
        </p>
      </section>

      <div className="card">
        <div className="panel-head"><h3>Reported to the police</h3><span className="hint">{status === 'ok' ? `${items.length} · newest first` : ''}</span></div>
        <div className="ledger">
          {status === 'loading' && (
            <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>Loading…</div>
          )}
          {status === 'error' && (
            <div style={{ padding: 16, color: 'var(--bad)', fontSize: 13 }}>
              Couldn’t load your notifications — your session may be locked or you’re offline. This is
              <b> not</b> confirmation that there are none.{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); window.location.reload(); }}>Reload</a>.
            </div>
          )}
          {status === 'ok' && items.length === 0 && (
            <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>
              No notifications recorded yet. Open “What I need to do” and record one against a deadline.
            </div>
          )}
          {status === 'ok' && items.map((n) => (
            <div className="ledger-row" key={n.uuid} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', gap: 8, width: '100%', alignItems: 'flex-start' }}>
                <span className="badge badge-ok">{n.rule_id ?? 'NOTIFY'}</span>
                <div className="ledger-main">
                  <div className="ledger-text">
                    <strong>{n.what}</strong>
                    {n.method ? <span className="muted"> · {METHOD[n.method] ?? n.method}{NON_DISCHARGING.has(n.method) ? <span style={{ color: 'var(--warn)' }}> (does not discharge)</span> : null}</span> : null}
                    {n.reference ? <span className="muted3 mono"> · {n.reference}</span> : null}
                  </div>
                  <div className="ledger-time">told police {n.notified_date} · anchored at ledger seq {n.ledger_seq}</div>
                </div>
              </div>
              <div style={{ width: '100%', paddingLeft: 8, marginTop: 6 }}>
                <Attachments entityType="notification" entityUuid={n.uuid} />
              </div>
            </div>
          ))}
        </div>
        <div className="panel-foot mono">every notification = one append-only ledger row</div>
      </div>
    </Page>
  );
}
